import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Input } from "@shadcn-in-nx/ui/input";
import { Search, Building, Coins, Loader2 } from "lucide-react";
import { useManageCompany } from "./ManageCompanyContext";

export default function CompanyPicker() {
    const { t } = useTranslation();
    const { selectCompany } = useManageCompany();
    const [search, setSearch] = useState("");
    const [companies, setCompanies] = useState([]);
    const debounceRef = useRef(null);
    const { call: fetchCompanies, isLoading } = useBluelagoon({ method: "getCompaniesForCredits" });

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            fetchCompanies({ search: search.trim() || undefined, limit: 50 })
                .then((res) => setCompanies(res?.data || []))
                .catch(() => setCompanies([]));
        }, 250);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [search, fetchCompanies]);

    return (
        <div className="space-y-3 max-w-2xl mx-auto">
            <p className="text-sm text-muted-foreground">
                {t("admin.manageCompany.picker.prompt", "Search and select a company to manage")}
            </p>
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                    autoFocus
                    placeholder={t("admin.manageCompany.picker.placeholder", "Search by name, ID, or user email...")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                />
            </div>
            <div className="border rounded divide-y max-h-[60vh] overflow-y-auto">
                {isLoading && companies.length === 0 ? (
                    <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                ) : companies.length === 0 ? (
                    <div className="text-center text-muted-foreground py-8 text-sm">
                        {t("admin.manageCompany.picker.empty", "No companies found")}
                    </div>
                ) : companies.map((c) => (
                    <button
                        key={c.id}
                        type="button"
                        onClick={() => selectCompany(c.id)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors text-left"
                    >
                        <div className="flex items-center gap-3 min-w-0">
                            <Building className="h-4 w-4 text-muted-foreground shrink-0" />
                            <div className="min-w-0">
                                <div className="text-sm font-medium truncate">{c.name}</div>
                                <div className="text-xs text-muted-foreground font-mono truncate">{c.id}</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                            <Coins className="h-3 w-3 text-yellow-500" />
                            {(c.currentCredits || 0).toFixed(2)}
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
}
